import { useMemo } from "react"
import { GridRowId } from "@mui/x-data-grid"

import { TrialResult } from "interface/trial"

import { GridColumnDefMap } from "components/grid/types"
import { columnBase } from "components/grid/util"
import { renderer } from "components/grid/renderer"
import { dateStampFormat, distinct, formatDateISO, safeParseDate } from "shared/util"

const IgnoreDrugNames = ['未定', 'なし', '「なし」', '該当せず', '-', 'ー', '‐']

const isOpenBlacket = (str: string, closedInit: boolean) => {
	let closed = closedInit
	let pos = 0
	do {
		const s = str.indexOf(closed ? ')' : '(', pos)
		if (s < 0) break
		closed = !closed
		pos = s + 1
	} while(pos >= 0)
	return closed
}

const strip = (text: string | undefined | null) => text?.replace(/<[^>]*>?/gm, '')

const splitDrugName = (drugNames: string | null, prefix: string) => {
	const names = drugNames?.replaceAll('　', ' ')
		.split(/[、|・|/|,]/g)
		.flatMap(d => {
			const trimed = strip(d?.trim().normalize('NFKC')) ?? ''
			if (trimed === '') return []
			if (IgnoreDrugNames.includes(trimed)) return []
			return trimed
		}) ?? []

	const joined = []
	let line = null
	let inBlacket = false
	for(const name of names) {
		if (line === null) {
			line = name
		} else if (inBlacket || /^\d/.test(name)) {
			line += `, ${name}`
		} else {
			joined.push(line)
			line = name
		}
		inBlacket = isOpenBlacket(name, inBlacket)
	}
	if (line !== null) joined.push(line)

	return distinct(prefix ? joined.map(d => `${prefix}${d}`) : joined)
}

const splitDiseaseName = (diseaseNames: string | null) => {
	const names = diseaseNames?.replaceAll('　', ' ')
		.split(/[、|,]/g)
		.flatMap(d => {
			const trimed = strip(d?.trim().normalize('NFKC')) ?? ''
			if (trimed === '') return []
			return trimed.replace(/癌/g, 'がん')
		}) ?? []

	const joined = []
	let line = null
	let inBlacket = false
	for(const name of names) {
		if (line === null) {
			line = name
		} else if (inBlacket || /^かつ/.test(name)) {
			line += name
		} else {
			joined.push(line)
			line = name
		}
		inBlacket = isOpenBlacket(name, inBlacket)
	}
	if (line !== null) joined.push(line)
	return joined
}

type ParsedUnit = {
  value: number
  unit: string
}
const parseUnits = (input?: string | null): ParsedUnit[] => {
  const results: ParsedUnit[] = []
  const parts = input?.trim().split(/\s+/) ?? []
  for (const part of parts) {
    const match = part.match(/^(\d+)(.+)$/)
    if (match && match.length === 3) {
      const value = parseInt(match[1], 10)
      const unit = match[2]
      if (!isNaN(value) && value > 0) {
        results.push({ value, unit })
      }
    }
  }
  return results
}

type useTrialGridColumnsProps = {
	onClickLink: (id: GridRowId) => void
}
export const useTrialGridColumns = ({
	onClickLink
}: useTrialGridColumnsProps) => {
	const columnsDef = useMemo<GridColumnDefMap<TrialResult>>(
		() => {
			const columnsDef: GridColumnDefMap<TrialResult> = {
				'link': {
					headerName: '詳細',
					hideable: false,
					sticky: true,
					value: (r) => r.trialId,
					...columnBase.action.openById(onClickLink),
				},
				'siteKey': {
					headerName: 'JRCTID',
					width: 138,
					value: (r) => r.siteKey
				},
				'statusText': {
					headerName: '状況',
					value: (r) => r.statusText,
					filterType: 'candidates'
				},
				'phase': {
					headerName: 'フェーズ',
					value: (r) => r.phase,
					filterType: 'candidates'
				},
				'progress': {
					headerName: '進捗',
					value: (r) => r.progress,
					filterType: 'candidates'
				},
				'title': {
					headerName: '研究名称',
					width: 500,
					sticky: true,
					value: (r) => strip(r.title),
					filterType: 'text'
				},
				'publicTitle': {
					headerName: '平易な研究名称',
					width: 500,
					defaultShow: false,
					value: (r) => strip(r.publicTitle),
					filterType: 'text'
				},
				'trialType': {
					headerName: '種別',
					width: 120,
					value: (r) => r.trialType,
					filterType: 'candidates'
				},
				'clinicalTrialsType': {
					headerName: '区分',
					width: 180,
					value: (r) => r.clinicalTrialsType,
					filterType: 'candidates'
				},
				'disease': {
					headerName: '疾患名',
					width: 280,
					value: (r) => splitDiseaseName(r.disease),
					renderCell: (params) => renderer.multiRow(params.value, 'vertical', 5),
					filterType: 'candidates'
				},
				'drugNames': {
					headerName: '薬剤名',
					width: 400,
					value: (r) => splitDrugName(r.drugBrandNames, '').concat(splitDrugName(r.drugGenericNames, '【般】')),
					renderCell: (params) => renderer.multiRow(params.value, 'vertical', 5),
					filterType: 'candidates'
				},
				'targetAge': {
					headerName: '対象年齢',
					width: 110,
					value: (r) => {
						const from = parseUnits(r.age?.lower).map(parsed => `${parsed.value}${parsed.unit}`).join(' ')
						const to = parseUnits(r.age?.upper).map(parsed => `${parsed.value}${parsed.unit}`).join(' ')
						return (from || to) ? `${from} ～ ${to}` : null
					},
					filterType: 'candidates'
				},
				'countries': {
					headerName: '日本以外の実施国',
					width: 500,
					defaultShow: false,
					value: (r) => r.countries?.split('/'),
					renderCell: (params) => renderer.multiRow(params.value, 'horizon', 15),
					filterType: 'sentense'
				},
				'sampleSize': {
					headerName: '患者数',
					value: (r) => r.sampleSize,
					...columnBase.personCount
				},
				'studyPurpose': {
					headerName: '試験の概要',
					defaultShow: false,
					width: 500,
					value: (r) => r.studyPurpose,
					renderCell: (params) => renderer.clampText(params.value, 5),
					filterType: 'sentense'
				},
				'interventionDescription': {
					headerName: '介入の内容',
					defaultShow: false,
					width: 500,
					value: (r) => r.interventionDescription,
					renderCell: (params) => renderer.clampText(params.value, 5),
					filterType: 'sentense'
				},
				'sponsor': {
					headerName: '依頼者',
					width: 240,
					value: (r) => r.sponsor,
					filterType: 'candidates'
				},
				'sisetu': {
					headerName: '実施施設',
					width: 600,
					limitRow: 5,
					value: (r) => r.sisetu?.flatMap(sisetu => sisetu.sisetuName ?? []),
					renderCell: (params) => renderer.multiRow(params.value, 'vertical', 5),
					filterType: 'candidates'
				},
				'block': {
					headerName: 'ブロック',
					width: 100,
					defaultShow: false,
					value: (r) => distinct(r.sisetu?.flatMap(sisetu => sisetu.blockName ?? []) ?? []),
					renderCell: (params) => renderer.multiRow(params.value, "vertical", 5),
					filterType: 'candidates'
				},
				'todofuken': {
					headerName: '都道府県',
					width: 100,
					defaultShow: false,
					value: (r) => distinct(r.sisetu?.flatMap(sisetu => sisetu.prefectureName ?? []) ?? []),
					renderCell: (params) => renderer.multiRow(params.value, "vertical", 5),
					filterType: 'candidates'
				},
				'persons': {
					headerName: '責任医師',
					width: 200,
					limitRow: 5,
					value: (r) => distinct(
						r.persons?.flatMap(person => person.kojinCode ? person.personName?.replaceAll('　', ' ') : []) ?? []
					),
					renderCell: (params) => renderer.multiRow(params.value, 'horizon'),
					filterType: 'candidates'
				},
				'registrationDate': {
					headerName: '登録/届出日',
					value: (r) => {
						if (r.registrationDate) {
							const dt = safeParseDate(r.registrationDate)
							if (dt) return formatDateISO(dt)
							return r.registrationDate
						} else if (r.notificationDate) {
							const dt = safeParseDate(r.notificationDate)
							if (dt) return formatDateISO(dt)
							return r.notificationDate
						}
						return null
					},
					...columnBase.date
				},
				'firstPublicDate': {
					headerName: '公表開始日',
					value: (r) => {
						const dt = safeParseDate(r.firstPublicDate)
						if (dt) return formatDateISO(dt)
						return r.firstPublicDate
					},
					...columnBase.date
				},
				'finalPublicDate': {
					headerName: '公表終了日',
					value: (r) => {
						const dt = safeParseDate(r.finalPublicDate)
						if (dt) return formatDateISO(dt)
						return r.finalPublicDate
					},
					...columnBase.date
				},
				'startDate': {
					headerName: '実施開始日',
					value: (r) => {
						if (r.startDate === '実施計画の公表日') {
							const dt = safeParseDate(r.firstPublicDate)
							if (dt) return formatDateISO(dt)
							return r.firstPublicDate
						}
						const dt = safeParseDate(r.startDate)
						if (dt) return formatDateISO(dt)
						return r.startDate
					},
					...columnBase.date
				},
				'endDate': {
					headerName: '実施終了日',
					value: (r) => {
						const dt = safeParseDate(r.endDate)
						if (dt) return formatDateISO(dt)
						return r.endDate
					},
					...columnBase.date
				},
				'terminalDate': {
					headerName: '中止日',
					value: (r) => dateStampFormat(r.terminalDate) ?? r.terminalDate,
					...columnBase.date
				}
			}
			return columnsDef
		},
		[onClickLink]
	)
	return { columnsDef }
}